import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Avatar, Button, Divider, Dropdown, Empty, Flex, Input, Typography, theme } from 'antd';
import {
  PlusCircleTwoTone,
  CheckOutlined,
  DownOutlined,
  LogoutOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import useStore from '../../store/useStore';

/** Үүнээс олон бизнестэй үед л хайлтын талбар гарна */
const SEARCH_THRESHOLD = 5;

/**
 * Сонгогдсон бизнесийн товчны хамгийн их өргөн. Хатуу өргөн ХЭРЭГЛЭХГҮЙ —
 * өмнө нь 183px байсан тул урт нэр байнга тайрагдаж, богино нэр дээр
 * хоосон зай үлддэг байв.
 */
const TRIGGER_MAX_WIDTH = 220;

/** Бизнесийн логоны хэмжээ */
const LOGO_SIZE = 26;

/**
 * Бизнесийн лого.
 *
 * `brandLogo` байхгүй эсвэл татагдаагүй үед нэрийн эхний үсэг гарна — өмнө нь
 * `Image` хоосон/эвдэрсэн дөрвөлжин зурагдаж байв.
 */
const MerchantLogo = ({ name, src, size = LOGO_SIZE }) => (
  <Avatar
    shape="square"
    size={size}
    src={src || undefined}
    alt={name}
    style={{ flex: 'none', borderRadius: 6, boxShadow: '2px 2px 4px 0px #00000040' }}
  >
    {(name ?? '?').trim().charAt(0).toUpperCase() || '?'}
  </Avatar>
);

const Profile = () => {
  const history = useHistory();
  const { token } = theme.useToken();

  const merchantCodeList = useStore((state) => state.user.merchantCodeList);
  const merchantCode = useStore((state) => state.user.merchantCode);
  const meta = useStore((state) => state.user.meta);
  const merchant = meta?.find((el) => el?.code === merchantCode);
  const merchantName = merchant?.name ?? merchantCode;

  const changeMerchant = useStore((state) => state.changeMerchant);

  const isEmployee = meta?.[0]?.isEmployee;

  /** Доод талын үйлдлүүд нь Menu item биш тул dropdown-г өөрсдөө хаана */
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Бизнес солих хүсэлт явж байх үед trigger дээр spinner харуулж, давхар дарахаас сэргийлнэ
  const [switchingMerchant, setSwitchingMerchant] = useState(false);
  const searchRef = useRef(null);

  const showSearch = merchantCodeList?.length > SEARCH_THRESHOLD;

  /** Нээгдэх бүрд хайлт цэвэрлэгдэж, талбар руу фокус шилжинэ */
  const onOpenChange = (next) => {
    setOpen(next);
    if (!next) setSearch('');
  };

  useEffect(() => {
    if (!open || !showSearch) return undefined;
    /** Overlay-н нээгдэх animation дуусахаас өмнө focus хийвэл алдагдана */
    const timer = setTimeout(() => searchRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [open, showSearch]);

  const onChangeMerchant = (code) => {
    if (switchingMerchant) return;
    setSwitchingMerchant(true);
    Promise.resolve(changeMerchant(code)).finally(() => setSwitchingMerchant(false));
  };

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/';
  };

  const addBusiness = () => {
    setOpen(false);
    history.push('/request', {
      from: 'profileMenu',
    });
  };

  /** Нэр эсвэл кодоор нь шүүнэ */
  const keyword = search.trim().toLowerCase();
  const filteredCodeList = merchantCodeList?.filter((code) => {
    if (!keyword) return true;
    const item = meta?.find((el) => el?.code === code);
    return (
      (item?.name ?? '').toLowerCase().includes(keyword) || code.toLowerCase().includes(keyword)
    );
  });

  /**
   * Сонгогдсон бизнесийн мөр — бүдэг цэнхэр дэвсгэр, зөөлөн цэнхэр сүүдэр.
   * Өнгийг брэндийн `colorPrimary`-аас гаралтай token-оор авна (хатуу hex биш).
   *
   * Инлайн style нь antd-ийн `:hover` дэвсгэрийг дардаг — сонгогдсон мөр
   * дээр дарах үйлдэл байхгүй тул тодруулга нь хөвүүлэхэд ч хэвээр үлдэнэ.
   */
  const activeItemStyle = {
    backgroundColor: token.colorPrimaryBgHover,
    cursor: 'default',
  };

  const menuItemsBusiness = filteredCodeList?.map((code) => {
    const item = meta?.find((el) => el?.code === code);
    const isActive = merchantCode === code;

    return {
      key: code,
      onClick: () => !isActive && onChangeMerchant(code),
      style: isActive ? activeItemStyle : undefined,
      label: (
        <Flex align="center" gap={10}>
          <MerchantLogo name={item?.name ?? code} src={item?.brandLogo} />
          <Typography.Text ellipsis strong={isActive} style={{ flex: 1 }}>
            {item?.name ?? code}
          </Typography.Text>
          {isActive && <CheckOutlined style={{ color: token.colorPrimary }} />}
        </Flex>
      ),
    };
  });

  /**
   * Бизнесийн жагсаалт л гүйнэ — "Бизнес нэмэх" / "Гарах" үйлдлүүд доод талд
   * бэхлэгдэж, гүйлгэхэд нуугдахгүй.
   */
  const dropdownFooter = (
    <Flex vertical gap={4} style={{ padding: 8 }}>
      {!isEmployee && (
        <Button
          block
          type="text"
          icon={<PlusCircleTwoTone style={{ fontSize: 18 }} />}
          onClick={addBusiness}
          style={{ justifyContent: 'flex-start', color: token.colorPrimary }}
        >
          Бизнес нэмэх
        </Button>
      )}
      <Button
        block
        danger
        type="text"
        icon={<LogoutOutlined />}
        onClick={logout}
        style={{ justifyContent: 'flex-start' }}
      >
        Гарах
      </Button>
    </Flex>
  );

  return (
    <div className="profile">
      {merchantCodeList?.length ? (
        <Dropdown
          menu={{ items: menuItemsBusiness }}
          trigger={['click']}
          placement="bottomRight"
          open={open}
          onOpenChange={onOpenChange}
          dropdownRender={(menu) => (
            <div
              style={{
                width: 'max-content',
                minWidth: 350,
                maxWidth: 'min(520px, calc(100vw - 32px))',
                backgroundColor: token.colorBgElevated,
                borderRadius: token.borderRadiusLG,
                boxShadow: token.boxShadowSecondary,
              }}
            >
              {showSearch && (
                <div style={{ padding: 8 }}>
                  <Input
                    ref={searchRef}
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Бизнес хайх"
                    prefix={<SearchOutlined style={{ color: token.colorTextPlaceholder }} />}
                    /** Esc-ээс бусад товчлуур Menu-н keyboard navigation-д баригдахгүй */
                    onKeyDown={(e) => e.key !== 'Escape' && e.stopPropagation()}
                  />
                </div>
              )}
              {menuItemsBusiness?.length ? (
                React.cloneElement(menu, {
                  style: {
                    maxHeight: 500,
                    overflowY: 'auto',
                    boxShadow: 'none',
                    /**
                     * `overflowY: auto` нь хэвтээ тэнхлэгийг ч хайчилдаг тул
                     * сонгогдсон мөрний сүүдэр багтахаар зайг өргөсгөв
                     * (antd-ийн анхны зай 4px нь хүрэлцэхгүй).
                     */
                    padding: 8,
                  },
                })
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Илэрц олдсонгүй"
                  style={{ margin: '16px 0' }}
                />
              )}
              <Divider style={{ margin: 0 }} />
              {dropdownFooter}
            </div>
          )}
        >
          <Button
            size="large"
            loading={switchingMerchant}
            style={{ maxWidth: TRIGGER_MAX_WIDTH, paddingInline: 10 }}
          >
            <Flex align="center" gap={10} style={{ flex: 1, minWidth: 0 }}>
              <MerchantLogo name={merchantName} src={merchant?.brandLogo} />
              {/* Тайрагдсан үед л tooltip-оор бүтэн нэрийг харуулна */}
              <Typography.Text
                ellipsis={{ tooltip: merchantName }}
                style={{ flex: 1, minWidth: 0, textAlign: 'start' }}
              >
                {merchantName}
              </Typography.Text>
              <DownOutlined
                style={{
                  flex: 'none',
                  fontSize: 12,
                  color: token.colorTextTertiary,
                  // Нээлттэй эсэхийг сум өөрөө мэдэгдэнэ
                  transform: open ? 'rotate(180deg)' : undefined,
                  transition: `transform ${token.motionDurationMid}`,
                }}
              />
            </Flex>
          </Button>
        </Dropdown>
      ) : (
        <Button type="text" icon={<LogoutOutlined />} onClick={logout}>
          Гарах
        </Button>
      )}
    </div>
  );
};

export default Profile;
